<template>
  <panel ref="panel" :title="`${$tc('tournament.registered_referees')} (${referees.length})`" collapsable>
    <template v-slot:actions>
      <el-button
        v-if="canEdit"
        type="primary"
        icon="el-icon-plus"
        size="medium"
        round
        @click="$refs.panel.expanded = true; openManageReferee = true">
        {{ $tc('tournament.register_referee') }}
      </el-button>

      <el-tooltip v-if="referees.length" :content="$tc('general.print')" placement="top">
        <el-button
          icon="el-icon-printer"
          size="medium"
          circle
          @click="openPrint($tc('club.referee', 2), $refs.printTable ? $refs.printTable.$el : null)" />
      </el-tooltip>
    </template>

    <referees-table
      :data="referees"
      :can-edit="canEdit"
      :show-club-name="showClubName"
      custom-logic
      hide-edit
      @edit="openEdit($event)"
      @custom-delete="onRemove($event)" />

    <print-referees-table ref="printTable" v-show="false" :data="referees" :show-club-name="showClubName" :tournament="tournament" />

    <main-drawer v-if="openManageReferee" :title="$tc('tournament.registered_referees')" @close="onClose()">
      <main-select v-model="form.refereeId" :label="$tc('club.referee')" :options="refereeOptions" :validator="$v.form.refereeId" />

      <template v-slot:footer>
        <div class="flex-buttons">
          <el-button @click="onClose()">{{ $tc('general.cancel') }}</el-button>
          <el-button type="primary" @click="addReferee()">{{ $tc('tournament.register_referee') }}</el-button>
        </div>
      </template>
    </main-drawer>
  </panel>
</template>

<script>
import openPrint from '@/common/print'
import { required } from 'vuelidate/lib/validators'
import errorHandler from '@/common/errorHandler.js'

import RefereesTable from './RefereesTable'
import PrintRefereesTable from './PrintRefereesTable'

export default {
  components: {
    RefereesTable,
    PrintRefereesTable
  },

  props: {
    club: {
      type: Object,
      default: null
    },

    tournament: {
      type: Object,
      required: true
    },

    showClubName: {
      type: Boolean,
      default: false
    },

    canEdit: {
      type: Boolean,
      default: false
    },

    referees: {
      type: Array,
      default: null
    }
  },

  data () {
    return {
      clubId: null,
      form: {
        refereeId: null
      },
      openPrint,
      openManageReferee: false
    }
  },

  computed: {
    user () {
      return this.$store.state.auth.user
    },

    refereeOptions () {
      const referees = this.club?.referees || this.user.club?.referees || []
      const filteredReferees = referees.filter(referee => referee.isActive)

      return filteredReferees.map(referee => ({
        text: referee.firstName + ' ' + referee.lastName,
        id: referee.id,
        disabled: this.referees.some(r => r.id === referee.id)
      }))
    }
  },

  methods: {
    onClose () {
      this.form.refereeId = null
      this.$v.$reset()
      this.openManageReferee = false
    },

    async addReferee () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return
      this.loading = true

      try {
        const tournament = await this.$store.dispatch('tournaments/registerReferee', {
          id: this.tournament?.id,
          refereeId: this.form.refereeId
        })
        this.updateActiveTournament(tournament)
        this.$message.success(this.$tc('messages.success_registered_referee'))
        this.onClose()
      } catch (error) {
        errorHandler(error)
      } finally {
        this.loading = false
      }
    },

    async onRemove (referee) {
      try {
        const tournament = await this.$store.dispatch('tournaments/removeReferee', {
          id: this.tournament?.id,
          refereeId: referee.id
        })
        this.updateActiveTournament(tournament)
        this.$message.success(this.$tc('messages.success_removed_referee'))
      } catch (error) {
        errorHandler(error)
      } finally {
        this.loading = false
      }
    },

    updateActiveTournament (tournament) {
      if (tournament.id === this.tournament?.id) {
        this.$store.commit('app/setActiveTournament', tournament)
      }
    }
  },

  validations: {
    form: {
      refereeId: { required }
    }
  }
}
</script>
